import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivateChild, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {OrganizationType} from '@portal/api/enums/organization-type';
import {UsersOrganizationsPermissionsReadType} from '@portal/api/enums/users-organizations-permissions-read-type';
import {StorageService} from '@portal/shared/services/storage.service';
import {UserPermissionsService} from '@portal/shared/services/user-permissions.service';

@Injectable({
    providedIn: 'root'
})
export class AssociationPermissionsGuard implements CanActivateChild {
    constructor(
        private readonly _storageService: StorageService,
        private readonly _userPermissionsService: UserPermissionsService,
        private readonly _router: Router,
    ) {
    }

    canActivateChild(_childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
        const org = this._storageService.getOrg();
        if (!org || org.type !== OrganizationType.Association) {
            return of(true);
        }

        const requiredPermission = this.getRequiredPermission(state.url);
        if (!requiredPermission) {
            return of(true);
        }

        if (this._userPermissionsService.currentPermissions) {
            return of(this.resolvePermission(requiredPermission));
        }

        return this._userPermissionsService.loadPermissions().pipe(
            map(() => this.resolvePermission(requiredPermission)),
            catchError(() => of(this.redirectHome()))
        );
    }

    private resolvePermission(permission: UsersOrganizationsPermissionsReadType): boolean | UrlTree {
        const hasPermission = this._userPermissionsService.hasOrganizationReadPermissionValue(permission);
        return hasPermission ? true : this.redirectHome();
    }

    private redirectHome(): UrlTree {
        return this._router.createUrlTree(['/organizations/details']);
    }

    private getRequiredPermission(url: string): UsersOrganizationsPermissionsReadType | null {
        if (url.startsWith('/party/donations')) {
            return UsersOrganizationsPermissionsReadType.DonationRead;
        }
        if (url.startsWith('/party/sponsors')) {
            return UsersOrganizationsPermissionsReadType.SponsorsRead;
        }
        if (url.startsWith('/party/revenues')) {
            return UsersOrganizationsPermissionsReadType.RevenuesRead;
        }
        if (url.startsWith('/party/expenses')) {
            return UsersOrganizationsPermissionsReadType.ExpensesRead;
        }
        if (url.startsWith('/party/bars')) {
            return UsersOrganizationsPermissionsReadType.BarsRead;
        }
        if (url.startsWith('/party/events')) {
            return UsersOrganizationsPermissionsReadType.EventsRead;
        }
        if (url.startsWith('/party/collaborators')) {
            return UsersOrganizationsPermissionsReadType.RevenuesRead;
        }
        if (url.startsWith('/organizations/users')) {
            return UsersOrganizationsPermissionsReadType.IsOwner;
        }

        return null;
    }
}
